import { useState, type FormEvent } from "react";
import Seo from "@/components/feature/Seo";
import Breadcrumb, { type Crumb } from "@/components/feature/Breadcrumb";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6g1urghk82orf0cl60";

const contacts = [
  {
    icon: "ri-mail-line",
    label: "商务与合作邮箱",
    value: "contact@51baoliao.com",
  },
  {
    icon: "ri-feedback-line",
    label: "爆料线索邮箱",
    value: "tips@51baoliao.com",
  },
  {
    icon: "ri-time-line",
    label: "响应时间",
    value: "工作日 9:00 - 18:00，线索 24 小时内跟进",
  },
];

const socials = [
  { icon: "ri-wechat-line", label: "微信公众号" },
  { icon: "ri-weibo-line", label: "官方微博" },
  { icon: "ri-tiktok-line", label: "抖音号" },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const crumbs: Crumb[] = [{ label: "联系反馈" }];

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Honeypot check - silently accept but do not actually submit
    const honeypot = (formData.get("website_alt") as string | null) ?? "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      form.reset();
      setMessage("");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请检查内容后重试。"
            : serverMsg || "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="bg-background-50">
      <Seo
        title="联系反馈 - 51爆料热点爆料平台"
        description="联系 51爆料：提交爆料线索、商务合作或内容反馈，我们会在工作时间内尽快回应。"
        keywords="51爆料联系方式,爆料线索,商务合作,内容反馈"
      />

      <div className="bg-secondary-500 text-white">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-6 py-10 md:py-14">
          <h1 className="text-2xl md:text-4xl font-heading font-black">
            联系与反馈
          </h1>
          <p className="mt-3 text-sm md:text-base text-white/85 max-w-2xl">
            无论是爆料线索、商务合作还是内容建议，我们都很想听到你的声音。
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 md:px-6 py-6">
        <Breadcrumb items={crumbs} />
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 md:px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
          <aside className="flex flex-col gap-5">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                联系方式
              </h2>
              <ul className="space-y-4">
                {contacts.map((c) => (
                  <li key={c.label} className="flex gap-3">
                    <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 text-xl">
                      <i className={c.icon} />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm text-foreground-500">
                        {c.label}
                      </span>
                      <span className="text-sm text-foreground-900 mt-0.5 break-all">
                        {c.value}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-6">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                关注我们
              </h2>
              <div className="flex flex-wrap gap-3">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href="#"
                    rel="nofollow"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-background-100 hover:bg-background-200 text-sm text-foreground-700 transition-colors cursor-pointer"
                  >
                    <i className={`${s.icon} text-lg`} />
                    {s.label}
                  </a>
                ))}
              </div>
            </div>

            <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
              <h2 className="flex items-center gap-2 text-base font-heading font-bold text-foreground-950 mb-2">
                <i className="ri-shield-check-line text-accent-600" />
                线索保护承诺
              </h2>
              <p className="text-sm text-foreground-700 leading-relaxed">
                我们承诺对线索来源严格保密，未经许可不会公开你的个人信息。
              </p>
            </div>
          </aside>

          <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
            <h2 className="text-xl font-heading font-black text-foreground-950">
              给我们留言
            </h2>
            <p className="text-sm text-foreground-500 mt-1">
              带 <span className="text-primary-600">*</span> 的为必填项
            </p>

            {status === "success" ? (
              <div className="mt-6 bg-primary-50 border border-primary-200 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500 text-white text-3xl">
                  <i className="ri-check-line" />
                </div>
                <p className="mt-4 text-base font-medium text-foreground-900">
                  提交成功，感谢你的反馈！
                </p>
                <p className="mt-1 text-sm text-foreground-500">
                  我们已收到你的留言，会尽快查看并回复。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-5 px-4 py-2 rounded-md border border-background-300 hover:border-primary-400 text-sm text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  再写一条
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="contact-name"
                      className="text-sm text-foreground-700"
                    >
                      姓名 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className="h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="contact-email"
                      className="text-sm text-foreground-700"
                    >
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收我们的回复"
                      className="h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="contact-message"
                    className="text-sm text-foreground-700"
                  >
                    留言内容 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="请描述你的爆料线索、合作需求或建议（不超过 500 字）"
                    className="px-3 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                  />
                  <span className="text-xs text-foreground-400 self-end">
                    {message.length} / 500
                  </span>
                </div>

                {/* Anti-spam field */}
                <input
                  type="text"
                  name="website_alt"
                  className="form-extra-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && (
                  <p className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
                    {formError}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="px-6 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {status === "submitting" ? "提交中..." : "提交反馈"}
                  </button>
                  <p className="text-xs text-foreground-400">
                    提交即表示你同意我们按隐私声明处理相关信息。
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}